import { useState } from "react";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";

import * as yup from "yup";

import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { confirmResetPassword } from "aws-amplify/auth";

import { RootState } from "@store/store";
import { PASSWORD } from "@helpers/regex";
import { setSignInStep } from "@store/slices/sessionSlice";

import { ArrowRight } from "lucide-react";

import { AppButton } from "@components/v2/AppButton";
import { PasswordField } from "./PasswordField";
import { primaryButtonSx } from "./loginStyles";

export const FormularioRecuperacionContrasenia = () => {
    const dispatch = useDispatch();

    const [error, setError] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);

    const accountRecover = useSelector((state: RootState) => state.session.recovery.username);

    const formik = useFormik({
        initialValues: {
            code: "",
            newPassword: "",
            email: "",
        },
        validationSchema: yup.object().shape({
            newPassword: yup
                .string()
                .matches(
                    PASSWORD,
                    "Formato incorrecto. Debe contener una letra mayúscula, número, carácter especial y más de 8 caracteres.",
                )
                .required("Campo requerido"),
            code: yup.string().required("Campo requerido"),
            email: yup.string().required("Campo requerido"),
        }),
        onSubmit: async (values) => {
            if (formik.isValid) {
                try {
                    setLoading(true);
                    setError(false);

                    await confirmResetPassword({
                        username: values.email,
                        confirmationCode: values.code,
                        newPassword: values.newPassword,
                    });

                    dispatch(setSignInStep("LOGIN"));
                } catch (error) {
                    setError(true);
                    console.log(error);
                    setLoading(false);
                }
            }
        },
    });

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Stack direction={"column"} sx={{ gap: 2.5 }}>
                <Box>
                    <Typography
                        sx={{
                            fontFamily: "'Outfit', system-ui, sans-serif",
                            fontSize: "32px",
                            fontWeight: 600,
                            letterSpacing: "-0.02em",
                            color: "text.primary",
                        }}>
                        ¡Hola de nuevo!
                    </Typography>
                    <Typography sx={{ fontSize: "15px", color: "text.secondary", mt: 1 }}>
                        Ingresa tu correo, la nueva contraseña y el código de verificación que se envió a tu correo.
                        {accountRecover.trim() == "" ? "" : ` de ${accountRecover}`}
                    </Typography>
                </Box>
                {accountRecover.trim() == "" && (
                    <TextField
                        name="email"
                        fullWidth
                        label="Correo"
                        type="email"
                        required
                        size="small"
                        autoComplete="off"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={(formik.touched.email && Boolean(formik.errors.email)) ?? false}
                        helperText={formik.touched.email && formik.errors.email}
                    />
                )}
                <PasswordField
                    name="newPassword"
                    fullWidth
                    label="Nueva contraseña"
                    required
                    size="small"
                    autoComplete="off"
                    slotProps={{
                        htmlInput: {
                            autoComplete: "new-password",
                        },
                    }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.newPassword && Boolean(formik.errors.newPassword)) ?? false}
                    helperText={formik.touched.newPassword && formik.errors.newPassword}
                />

                <PasswordField
                    name="code"
                    fullWidth
                    label="Código de verificación"
                    required
                    size="small"
                    autoComplete="off"
                    slotProps={{
                        htmlInput: {
                            autoComplete: "new-password",
                        },
                    }}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={(formik.touched.newPassword && Boolean(formik.errors.newPassword)) ?? false}
                    helperText={formik.touched.newPassword && formik.errors.newPassword}
                />
                {error && (
                    <Alert variant="outlined" severity="error">
                        Codigo incorrecto. Verifica el código de confirmación.
                    </Alert>
                )}
                <AppButton
                    tone="navy"
                    type="submit"
                    size="large"
                    endIcon={<ArrowRight size={18} strokeWidth={2.2} />}
                    sx={primaryButtonSx}
                >
                    Resetear contraseña
                </AppButton>
            </Stack>
        </form>
    );
};
